import { uniq } from '../utils'
import { AIOPS_APP_ID, CDL_APP_ID, CIE_APP_ID, FAWKES_APP_ID, PAE_APP_ID, RUNNING, SDWAN_APP_ID } from '../constants'

export interface Association {
  tenant_id: string
  app_id: string
  region?: string
  [key: string]: any
}
export interface Instance {
  tenant_id: string
  support_account_id: string
  app_id: string
  tsg_id?: string
  instance_id?: string
  status?: string
  url?: string
  associations?: Association[]
  region?: string
  platform_region?: string
  extra?: Record<string, any>
  runtime_attributes?: Record<string, string> & { cluster?: Record<string, string> }
  [key: string]: any
}

export const shouldOverwrite = (inst: Instance, map: Map<string, Instance>) => {
  const existing = map.get(inst.app_id)
  if (!existing) {
    return true
  }
  if (inst.status === RUNNING) {
    if (existing.status !== RUNNING) {
      return true
    }
    // temp behaviors
    if (!inst.iam_controlled || inst.extra?.hidden || inst.runtime_attributes?.hidden) {
      return false
    }
    return true
  }
  else if (existing.status !== RUNNING || !existing.iam_controlled ||
    existing.extra?.hidden || existing.runtime_attributes?.hidden) {
    return true
  }
  return false
}

export type TsgInstanceError = Error & { ipRestricted?: boolean }
export interface TsgInstances extends Array<Instance> {
  tsg_id?: string
  error?: TsgInstanceError
  has(app_id: string): boolean
  get(app_id: string): Instance | undefined
  isRunningStatus(app_id: string): boolean
  isFawkesRunning?: boolean
  getPlatformRegion(app_id?: string): string | undefined
  flags?: Record<string, any>
}

export type ReadonlyTsgInstances = Readonly<TsgInstances>

export type IndexTsgInstances = (
  (instances: ReadonlyTsgInstances | Instance[], tsg_id: string) => ReadonlyTsgInstances
) & { fromError: (error: Error, tsg_id: string) => ReadonlyTsgInstances }

export const indexTsgInstances: IndexTsgInstances = (instances, tsg_id) => {
  if (Object.isFrozen(instances) && Array.isArray(instances) && (instances as ReadonlyTsgInstances).get) {
    return instances as ReadonlyTsgInstances
  }
  const tsgInstances = Array.from(instances) as ReadonlyTsgInstances
  const map = new Map<string, Instance>()
  for (const inst of instances) {
    map.set(inst.tenant_id, inst)
    if (shouldOverwrite(inst, map)) {
      map.set(inst.app_id, inst)
    }
  }
  const isRunningStatus = (app_id: string) => {
    const inst = map.get(app_id)
    return Boolean(inst?.iam_controlled && inst.status === RUNNING)
  }
  const isFawkesRunning = () => {
    if (!map.size || !isRunningStatus(FAWKES_APP_ID)) {
      return false
    }
    return !map.has(PAE_APP_ID) || isRunningStatus(PAE_APP_ID)
  }
  const preCalcGetPlatformRegionFn = (): TsgInstances['getPlatformRegion'] => {
    const defaultPlatformRegionTryAppIds = [CDL_APP_ID, FAWKES_APP_ID, SDWAN_APP_ID, AIOPS_APP_ID, CIE_APP_ID]
    const getInstPlatformRegion = (inst: Instance | null | undefined): string | undefined => {
      if (!inst) {
        return undefined
      }
      if (inst.app_id === CDL_APP_ID || inst.app_id === CIE_APP_ID) {
        return inst.platform_region || inst.region
      }
      return inst.platform_region
    }
    const detectPlatformRegion = () => {
      if (!tsgInstances.length) {
        return undefined
      }
      for (const appId of defaultPlatformRegionTryAppIds) { // try provided appId, then CDL, PA, CIE first
        const platformRegion = getInstPlatformRegion(map.get(appId))
        if (platformRegion) {
          return platformRegion
        }
      }
      // try any instance's platform_region
      for (const inst of tsgInstances) {
        if (inst?.platform_region) {
          return inst.platform_region
        }
      }
    }
    const _cachedPlatformRegion = detectPlatformRegion()
    const samePlatformRegion = tsgInstances.every(inst =>
      getInstPlatformRegion(inst) === _cachedPlatformRegion
    )
    if (samePlatformRegion) { // should be in this case for most of TSGs
      return (app_id?: string) => _cachedPlatformRegion
    }
    return (app_id?: string) => {
      if (!tsgInstances.length) {
        return undefined
      }
      if (!app_id || app_id === CDL_APP_ID) {
        return _cachedPlatformRegion
      }
      const tryAppIds = uniq([app_id, ...defaultPlatformRegionTryAppIds])
      for (const appId of tryAppIds) { // try provided appId, then CDL, PA, CIE first
        const platformRegion = getInstPlatformRegion(map.get(appId))
        if (platformRegion) {
          return platformRegion
        }
      }
      return undefined
    }
  }
  Object.defineProperties(tsgInstances, {
    _map: { value: map },
    tsg_id: { value: tsg_id },
    get: { value: map.get.bind(map) },
    has: { value: map.has.bind(map) },
    isRunningStatus: { value: isRunningStatus },
    isFawkesRunning: { value: isFawkesRunning() }, // directly pre-calc value
    getPlatformRegion: { value: preCalcGetPlatformRegionFn() },
    flags: { value: (instances as any).flags || {} }
  })
  return Object.freeze(tsgInstances) as ReadonlyTsgInstances
}
indexTsgInstances.fromError = (error: Error, tsg_id) => {
  const map = new Map()
  const returnFalse = () => false
  const returnUndef = () => undefined
  const tsgInstance = Object.defineProperties<Instance[]>([], {
    _map: { value: map },
    tsg_id: { value: tsg_id },
    error: { value: error },
    get: { value: returnUndef },
    has: { value: returnFalse },
    isRunningStatus: { value: returnFalse },
    isFawkesRunning: { value: false },
    getPlatformRegion: { value: returnUndef },
    flags: { value: {} },
  }) as ReadonlyTsgInstances
  return Object.freeze(tsgInstance) as ReadonlyTsgInstances
}

export default indexTsgInstances
